import { useLazyQuery, useQuery } from '@apollo/client';
import { CURRENT_SUBSCRIPTION, CURRENT_USER } from '@repo-sapiens/graphql';
import { LoadingLayout } from '../components/layout/LoadingLayout';
import { useDemoMode } from '../hooks';
import React, { createContext, Dispatch, useEffect, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  E_UserAction,
  GetMeResponse,
  IUser,
  SubscriptionResponse,
} from '../types';
import { E_Route, ESubscriptionPlan } from '@repo-sapiens/constants';
import { PublicPages } from '../routes';

type TUserState = IUser;

type TUserAction =
  | { type: E_UserAction.SET_USER; payload: TUserState }
  | { type: E_UserAction.CLEAR };

type TContext = {
  state: TUserState;
  dispatch: Dispatch<TUserAction>;
};

const initialState = {} as TUserState;

export const UserContext = createContext({} as TContext);

function userReducer(_state: TUserState, action: TUserAction): TUserState {
  switch (action.type) {
    case E_UserAction.SET_USER:
      return action.payload;
    case E_UserAction.CLEAR:
      return initialState;

    default:
      throw Error('Wrong value');
  }
}

type TUserContextProviderProps = {
  children: React.ReactNode;
};
export const UserContextProvider: React.FC<TUserContextProviderProps> =
  React.memo(({ children }) => {
    const [state, dispatch] = useReducer(userReducer, initialState);
    const [loadUser, { loading }] = useLazyQuery<GetMeResponse>(CURRENT_USER);

    const { demoMode } = useDemoMode();

    const { data: subscriptionData, loading: subLoading } =
      useQuery<SubscriptionResponse>(CURRENT_SUBSCRIPTION);

    const isFree = subscriptionData?.currentSubscription?.plan === 'free';
    const isCanceled =
      subscriptionData?.currentSubscription?.plan ===
      ESubscriptionPlan.Canceled;

    const navigate = useNavigate();

    useEffect(() => {
      const publicRoutes = PublicPages.map((r) => r.path.toString());

      const current = window.location.pathname;

      if (!publicRoutes.includes(current)) {
        loadUser({ fetchPolicy: 'network-only' }).then((res) => {
          if (!res.data?.me) {
            navigate(E_Route.AuthBegin);
            return;
          }
          const vipUser = res.data?.me?.vipUser;
          dispatch({
            type: E_UserAction.SET_USER,
            payload: res.data?.me as TUserState,
          });

          const partialAccount =
            res.data?.me?.partialAccount ||
            (!res.data?.me?.hasGmail && !res.data?.me?.hasCalendar) ||
            res.data?.me?.invalidGrant;
          if (partialAccount && !demoMode) {
            navigate(E_Route.Onboarding);
          }

          if (
            isCanceled &&
            !demoMode &&
            !vipUser &&
            ![E_Route.Onboarding].includes(current as E_Route)
          ) {
            navigate(E_Route.ChoosePlan);
          }

          const freeForbiddenRoutes = [
            E_Route.Podcast,
            E_Route.Meeting,
            E_Route.Calendar,
            E_Route.Todos,
            E_Route.Transcript,
          ];
          let forbidden = false;
          freeForbiddenRoutes.forEach((route) => {
            if (route.includes(current)) {
              forbidden = true;
            }
          });
          if (isFree && !vipUser && !demoMode && forbidden) {
            navigate(E_Route.Home);
          }
        });
      }
    }, [loadUser, navigate, demoMode, isFree, isCanceled]);

    return (
      <UserContext.Provider value={{ state, dispatch }}>
        {loading || subLoading ? (
          <div
            style={{
              height: '99.5vh',
              width: 'calc(100vw - 100px)',
              marginLeft: '100px',
            }}
          >
            <LoadingLayout />
          </div>
        ) : (
          children
        )}
      </UserContext.Provider>
    );
  });
